/**
 * Champagne Wine Shop - uniform product-card imagery.
 *
 * The catalogue is a mix of tight bottle crops, bottles floating in large empty canvases,
 * framed shots and the odd landscape photo, so a plain object-fit rule cannot make the grid
 * look even. This script reads a lookup table measured off every product image (the bounding
 * box of the actual bottle, the image aspect ratio and the background colour) and does two
 * things with it.
 *
 * 1. Placement. Each card image is positioned so that every bottle is rendered at the same
 *    height, on the same baseline, centred on the bottle rather than on the canvas. Only
 *    bottle-shaped content is taken to full height - a jar, a gift box or a photo of six
 *    bottles side by side would look absurd blown up to bottle height, so the target height
 *    tapers off as the content gets wider. Nothing is ever stretched.
 *
 * 2. Background. Where the measurement says the shot sits on a flat light background, a worker
 *    lifts that background off and the card swaps to the transparent version, so the whole grid
 *    reads as bottles standing on the shop's own dark panel instead of a patchwork of white
 *    boxes. Until the worker answers, the panel is painted with the image's own background
 *    colour, so there is never a visible seam. Results are cached, so this is paid once.
 *
 * Images whose filename contains "cws-" have already been rebuilt permanently by the
 * conversion pass and are left alone.
 *
 * Table format: |<productId>=<x>,<y>,<w>,<h>,<aspect>,<bg>|...
 * x/y/w/h/aspect are thousandths; bg is a hex colour without '#', or 'T' (transparent)
 * or 'X' (not a flat colour).
 */
(function () {
  if (window.__wineMediaNormalize) return;
  window.__wineMediaNormalize = true;

  var script = document.currentScript;
  var SOURCE = script && script.dataset.boxes;
  var WORKER_URL = script && script.dataset.worker;
  if (!SOURCE) return;

  /* height of a bottle as a fraction of the card media height */
  var TARGET_H = 0.86;
  /* content this narrow (width/height) is treated as a bottle and gets the full height */
  var BOTTLE_AR = 0.42;
  /* content this wide (a jar, a gift box, a row of bottles) is held back to SQUAT_H */
  var SQUAT_AR = 1.0;
  var SQUAT_H = 0.58;
  /* widest the bottle may get, as a fraction of the card media width */
  var TARGET_W = 0.84;
  /* gap between the foot of the bottle and the bottom of the card media */
  var PAD_BOTTOM = 0.07;
  /* filename marker written by the permanent conversion pass */
  var CLEANED = /cws-\d/;
  /* set window.WINE_KNOCKOUT = false to keep the painted panels instead */
  var KNOCKOUT = window.WINE_KNOCKOUT !== false;

  var table = null;
  var queued = false;
  var worker = null;
  var pending = Object.create(null);
  var seq = 0;

  function parse(text) {
    var out = Object.create(null);
    var rows = text.split('|');
    for (var i = 0; i < rows.length; i++) {
      var row = rows[i];
      if (!row) continue;
      var eq = row.indexOf('=');
      if (eq < 0) continue;
      var v = row.slice(eq + 1).split(',');
      if (v.length < 6) continue;
      out[row.slice(0, eq)] = {
        x: +v[0] / 1000,
        y: +v[1] / 1000,
        w: +v[2] / 1000,
        h: +v[3] / 1000,
        ar: +v[4] / 1000,
        bg: v[5]
      };
    }
    return out;
  }

  /* ---------- background removal ---------- */

  function getWorker() {
    if (worker || !WORKER_URL || typeof Worker === 'undefined' || typeof OffscreenCanvas === 'undefined') return worker;
    try {
      worker = new Worker(WORKER_URL);
      worker.onmessage = function (e) {
        var job = pending[e.data.key];
        delete pending[e.data.key];
        if (!job) return;
        if (!e.data.ok) return;
        var url = URL.createObjectURL(e.data.blob);
        var img = job.img;
        img.addEventListener('load', function () {
          job.panel.style.backgroundColor = '';
        }, { once: true });
        img.removeAttribute('srcset');
        img.removeAttribute('sizes');
        img.src = url;
      };
      worker.onerror = function () { worker = null; };
    } catch (e) {
      worker = null;
    }
    return worker;
  }

  function requestKnockout(img, panel) {
    if (!KNOCKOUT || img.dataset.wineKo) return;
    var w = getWorker();
    if (!w) return;
    var url = img.currentSrc || img.getAttribute('src');
    if (!url) return;
    img.dataset.wineKo = '1';
    var key = 'k' + seq++;
    pending[key] = { img: img, panel: panel };
    w.postMessage({ key: key, url: url });
  }

  /* ---------- placement ---------- */

  function apply(gallery) {
    var id = gallery.getAttribute('data-product-id');
    if (!id) return;

    var images = gallery.querySelectorAll('.product-media__image');
    if (images.length !== 1) return;
    var img = images[0];
    if (img.dataset.wineFit === id) return;

    var panel = img.closest('.product-media-container');
    if (!panel) return;

    /* already rebuilt permanently: a transparent PNG on a standard 4:5 canvas */
    if (CLEANED.test(img.getAttribute('src') || '')) {
      img.style.padding = '0';
      img.style.objectFit = 'contain';
      img.style.objectPosition = '50% 50%';
      img.dataset.wineFit = id;
      return;
    }

    var rec = table[id];
    if (!rec || !(rec.ar > 0) || !(rec.h > 0.02) || !(rec.w > 0.02)) return;
    var pw = panel.clientWidth;
    var ph = panel.clientHeight;
    if (!pw || !ph) return;
    var ratio = pw / ph;

    /* how bottle-like the content is: its own width / height */
    var contentAR = (rec.w * rec.ar) / rec.h;
    var targetH = TARGET_H;
    if (contentAR > BOTTLE_AR) {
      var t = Math.min(1, (contentAR - BOTTLE_AR) / (SQUAT_AR - BOTTLE_AR));
      targetH = TARGET_H - t * (TARGET_H - SQUAT_H);
    }

    /* image height, as a fraction of the panel height, that renders the content at targetH */
    var h = targetH / rec.h;
    var widthUsed = (rec.w * h * rec.ar) / ratio;
    if (widthUsed > TARGET_W) h *= TARGET_W / widthUsed;

    var centreX = rec.x + rec.w / 2;
    var below = (1 - (rec.y + rec.h)) * h;

    var s = img.style;
    s.position = 'absolute';
    s.left = '50%';
    s.top = 'auto';
    s.bottom = ((PAD_BOTTOM - below) * 100).toFixed(3) + '%';
    s.height = (h * 100).toFixed(3) + '%';
    s.width = 'auto';
    s.maxWidth = 'none';
    s.minWidth = '0';
    s.aspectRatio = String(rec.ar);
    s.objectFit = 'contain';
    s.objectPosition = '50% 50%';
    s.padding = '0';
    s.transform = 'translateX(' + (-centreX * 100).toFixed(3) + '%)';

    var flatColour = rec.bg !== 'T' && rec.bg !== 'X';
    if (flatColour && !img.dataset.wineKo) {
      panel.style.transition = 'background-color .25s ease';
      panel.style.backgroundColor = '#' + rec.bg;
      if (img.complete) requestKnockout(img, panel);
      else img.addEventListener('load', function () { requestKnockout(img, panel); }, { once: true });
    }

    img.dataset.wineFit = id;
  }

  function scan() {
    queued = false;
    if (!table) return;
    var galleries = document.querySelectorAll('.card-gallery[data-product-id]');
    for (var i = 0; i < galleries.length; i++) apply(galleries[i]);
  }

  function schedule() {
    if (queued) return;
    queued = true;
    requestAnimationFrame(scan);
  }

  fetch(SOURCE)
    .then(function (r) { return r.text(); })
    .then(function (t) { table = parse(t); scan(); })
    .catch(function () { /* leave the CSS-only fallback in place */ });

  document.addEventListener('DOMContentLoaded', schedule);
  window.addEventListener('load', schedule);
  window.addEventListener('resize', function () {
    document.querySelectorAll('.product-media__image[data-wine-fit]').forEach(function (img) {
      delete img.dataset.wineFit;
    });
    schedule();
  });

  new MutationObserver(schedule).observe(document.documentElement, { childList: true, subtree: true });
})();
